/**
 * Pilot-controlled lighting intensity
 */
export enum LightingIntensity {
  Low,
  Medium,
  High
}


/**
 * Model that captures information on how to activate pilot-controlled lighting at an airport (e.g., 5 clicks within
 * 7 seconds to activate medium-intensity lighting).
 */
export class PilotControlledLighting {
  /**
   * Unique identifier for this record in the database
   */
  pclId: number;

  /**
   * Airport record ID for which this data applies
   */
  airportId: number;

  /**
   * Source ID that identifies the source of this data (e.g., airport manager)
   */
  sourceId: number;

  /**
   * Lighting intensity (low, medium, or high)
   */
  intensity: LightingIntensity;

  /**
   * Number of clicks you must perform with a given duration of time to activate the lighting.
   */
  clicks: number;

  /**
   * Duration of time (in seconds) within which you must click the microphone a given number of times to activate
   * pilot-controlled lighting (null if no data).
   */
  seconds: number;

  /**
   * Parses pilot-controlled lighting
   * @param data {any} Pilot-controlled lighting data as returned by API
   * @return {PilotControlledLighting} Parsed pilot-controlled lighting
   */
  static parse(data: any): PilotControlledLighting {
    return {
      pclId: data.pclId,
      airportId: data.airportId,
      sourceId: data.sourceId,
      intensity: this.parseLightingIntensity(data.intensity),
      clicks: data.clicks,
      seconds: data.seconds
    };
  }

  /**
   * Parses lighting intensity
   * @param value {any} Lighting intensity as returned by API
   * @return {LightingIntensity} Parsed lighting intensity
   */
  static parseLightingIntensity(value: any): LightingIntensity {
    switch (value) {
      case 'low':
        return LightingIntensity.Low;
      case 'medium':
        return LightingIntensity.Medium;
      case 'high':
        return LightingIntensity.High;
      default:
        return null;
    }
  }

  /**
   * Formats lighting intensity for display
   * @param value {LightingIntensity} Lighting intensity enum value to be formatted as string
   * @return {string} formatted lighting intensity
   */
  static formatLightingIntensity(value: LightingIntensity): string {
    switch (value) {
      case LightingIntensity.Low:
        return 'low';
      case LightingIntensity.Medium:
        return 'medium';
      case LightingIntensity.High:
        return 'high';
      default:
        return 'on';
    }
  }
}
